import type {RuntimeLoaders} from "@polygonjs/js-core";

// Resolving the @polygonjs/js-core that matches a scene's engine version
// (PLAN-per-version-js-core.md phase C).
//
// Every scene is loaded with the engine version it was saved with (`scenes.polygon_version`, which
// only moves forward when the author asks), and this app used to pin ONE js-core in package.json for
// all of them. The boundary between the two is silent: the wasm's `env` import object is a Proxy that
// stubs any name js-core lacks, and a renamed export reads back `undefined` with nothing logged. It
// broke for real: engine v2.0.60 renamed the export `on_request_animation_frame`, and every wasm
// below it then rendered nothing under the 2.0.60 js-core.
//
// So the version selects BOTH halves. The wasm arrives through securewasm at
// /polygon-wasm/<version>/editor/…, and the JavaScript through /polygon-js/<version>/…, served off
// the same artifact volume by PolygonJsController. One number, no second thing to keep in step.
//
// `@polygonjs/js-core` stays in package.json as a TYPES-ONLY devDependency: every import in this file
// is `import type`, which TypeScript erases, so nothing here is resolved by the bundler and no
// version of js-core is baked into the deployed asset.

/** The base entry's module shape. Types only: the bundle itself arrives by URL at runtime. */
export type PolygonCoreModule = typeof import("@polygonjs/js-core");
/** The `webgpu` subpath, which is where the multi-scene embed entry point lives. */
export type PolygonWebGPUModule = typeof import("@polygonjs/js-core/webgpu");

/** One engine version's JavaScript: the base module plus the loaders the engine pulls in itself. */
export interface PolygonRuntime {
	version: string;
	core: PolygonCoreModule;
	loaders: RuntimeLoaders;
	webgpu: () => Promise<PolygonWebGPUModule>;
}

/**
 * How a module URL is fetched. Injected so the resolver can be tested without a real network or a
 * real bundle; production uses the dynamic import below.
 */
export type ModuleImporter = (url: string) => Promise<unknown>;

// `@vite-ignore` because the specifier is built at runtime. That is the point: Vite must NOT try to
// resolve or pre-bundle it, since the module being named does not exist in this repo at build time
// and lives on the artifact volume instead.
//
// Resolved against the DOCUMENT rather than left root-relative, because a root-relative specifier in
// an ES module resolves against the MODULE's own URL, not the page's. In production both are the
// Rails origin and the distinction never shows. In development they are two different servers:
// config/vite.json sets `skipProxy`, so the editor entry is served from http://localhost:3036 and
// every `/polygon-js/<version>/…` built here was being asked of Vite, which has no such route and
// answers 404. The browser reports that as "Failed to fetch dynamically imported module", naming the
// parent import, with nothing in the Rails log because the request never arrived.
//
// `document.baseURI` rather than `location.origin` so a page that ever grows a <base> is followed
// too. The fallback is for a context with no document at all, where a root-relative URL is the only
// thing left to try.
const importByUrl: ModuleImporter = (url) =>
	import(
		/* @vite-ignore */ typeof document === "undefined"
			? url
			: new URL(url, document.baseURI).href
	);

/**
 * Bare semver, the spelling the store directories use. The server strips a leading `v` too
 * (`PolygonVersions.normalize`), but the client must as well or a `v`-prefixed scene version
 * would produce a second URL, a second cache entry and a second module graph for the same bytes.
 */
export function polygonRuntimeVersion(version: string): string {
	return version.replace(/^v/, "");
}

/** The served root of one version's bundle. Every other URL in this file hangs off it. */
export function polygonRuntimeBase(version: string): string {
	return `/polygon-js/${polygonRuntimeVersion(version)}/`;
}

// The engine's own loader slots, each a fixed filename in the bundle. Fixed rather than hashed is a
// property of the build (vite.polygonjs-com.config.ts in the engine repo): internal chunks carry a
// content hash, entries do not, precisely so this map can be composed from the version alone with no
// manifest to fetch first.
//
// `threads` is deliberately absent. It only applies to a multithreaded wasm artifact served from a
// cross-origin-isolated page, which polygonjs.com is not; the engine derives the feature from the
// loader's presence, so offering it here would turn it on.
const LOADER_FILES = {
	editor: "editor.js",
	io: "io.js",
	overlay: "overlay.js",
	video: "video.js",
	webcam: "webcam.js",
	webGL: "webgl.js",
	webGPU: "webgpu.js",
} as const satisfies Partial<Record<keyof RuntimeLoaders, string>>;

function buildLoaders(base: string, importModule: ModuleImporter): RuntimeLoaders {
	const loaders: RuntimeLoaders = {};
	for (const [slot, file] of Object.entries(LOADER_FILES)) {
		// One cast, here. The engine types each slot with the module shape it expects, and a dynamic
		// import by URL is `Promise<unknown>` to TypeScript whatever the URL says; there is nothing more
		// specific to be had without re-declaring every loader module's shape by hand.
		loaders[slot as keyof typeof LOADER_FILES] = (() =>
			importModule(`${base}${file}`)) as never;
	}
	return loaders;
}

/**
 * Build a resolver over a given importer. One CACHE per resolver, keyed by version: a page showing
 * several scenes resolves the runtime once, and a Turbo session that visits two posts pinned to
 * different versions ends up with two entries and two independent module graphs. Caching the PROMISE
 * rather than the result is what makes two simultaneous callers share one download.
 */
export function createPolygonRuntimeResolver(importModule: ModuleImporter = importByUrl) {
	const cache = new Map<string, Promise<PolygonRuntime>>();

	return function resolvePolygonRuntime(requestedVersion: string): Promise<PolygonRuntime> {
		const version = polygonRuntimeVersion(requestedVersion);
		const cached = cache.get(version);
		if (cached) return cached;

		const base = polygonRuntimeBase(version);
		const pending = importModule(`${base}index.js`).then((module) => ({
			version,
			core: module as PolygonCoreModule,
			loaders: buildLoaders(base, importModule),
			webgpu: () => importModule(`${base}webgpu.js`) as Promise<PolygonWebGPUModule>,
		}));
		// A failed import must not poison the cache: the next attempt should be able to try again
		// (a transient 502 on the volume is a thing), and a rejected promise left in the map would
		// answer every later caller with the same old failure.
		void pending.catch(() => {
			if (cache.get(version) === pending) cache.delete(version);
		});
		cache.set(version, pending);
		return pending;
	};
}

/** The app's resolver. */
export const resolvePolygonRuntime = createPolygonRuntimeResolver();
